import { useDebouncedAutoSave } from '../../components/ui';
import { useId, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  Drawer,
  SaveHint,
  InlineError,
  Input,
  Select,
  Skeleton,
} from '../../components/ui';
import { RichTextEditor, RichTextView, isRichTextEmpty } from '../../components/RichText';
import { RowGroup } from '../../components/ui/RowGroup';
import { CommentsSection } from '../comments/CommentsSection';
import { SupplierReviewSection } from '../supplierReviews/SupplierReviewSection';
import { useReviewForTask } from '../supplierReviews/hooks';
import { FilesSection } from '../files/FilesSection';
import { useTaskFiles } from '../files/hooks';
import { groupIntoAssets } from '../files/types';
import { PanelFold } from './PanelFold';
import { useMuteTask, useMutedTaskIds, useUnmuteTask } from '../notifications/hooks';
import {
  useAddTaskCollaborator,
  useCreateSubtask,
  useTaskDefaults,
  useDeleteTask,
  useRemoveTaskCollaborator,
  useSetTaskCompleted,
  useTaskActivity,
  useTaskCollaborators,
  useUpdateTask,
} from '../tasks/hooks';
import { dueDateProblem } from '../tasks/taskLens';
import { taskErrorMessage } from '../tasks/queries';
import { PushToSocialDialog } from './PushToSocialDialog';
import { TASK_ACTIVITY_LABELS, TASK_PRIORITY_LABELS } from '../tasks/types';
import { InlineText, InlineTextInput, formatDate, memberLabel, statusTone } from './TaskTable';
import { FieldPicker } from '../fields/FieldPicker';
import type { SelectOption } from '../../components/ui';
import { CopyLinkButton } from '../sharing/CopyLinkButton';
import { taskShareUrl } from '../sharing/shareLinks';
import type { ProjectVisibility } from '../projects/types';
import type { Task, TaskPriority, TaskUpdate } from '../tasks/types';
import type { TaskLookups } from './TaskTable';

/**
 * The task details panel (Stage 10): everything about one task that does not
 * fit in a row. Description lives here because it is not a List View column,
 * subtasks live here because rows only show their count, and deletion lives
 * here behind an explicit confirmation. Assignee, status, priority, due date
 * and remarks stay editable in the row, so the panel shows them read-only.
 *
 * Subtasks carry a title, one assignee and a due date only (handoff 20) and
 * never a section. Completing a subtask is the same independent checkbox the
 * parent has: it never touches any status.
 *
 * Stage 11 adds the panel's Followers section — the task_collaborators table
 * created back in Stage 3 finally gets its interface — and the Activity
 * section, read straight from the trigger-written history with actor names.
 * Both load only when the panel opens, never with the task list.
 *
 * Phase 3 Stage 4 adds the Comments section between Followers and Activity:
 * the conversation belongs above the audit trail.
 *
 * Nothing here decides permission. canEdit hides controls as a convenience;
 * Row Level Security refuses anything the caller may not do.
 */

/** Same order the List View row offers, most urgent first. */
const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'high', label: TASK_PRIORITY_LABELS.high },
  { value: 'medium', label: TASK_PRIORITY_LABELS.medium },
  { value: 'low', label: TASK_PRIORITY_LABELS.low },
];

function SubtaskRow({
  subtask,
  lookups,
  canEdit,
  onEdit,
  onToggleComplete,
  onRemove,
}: {
  subtask: Task;
  lookups: TaskLookups;
  canEdit: boolean;
  onEdit: (task: Task, changes: TaskUpdate) => void;
  onToggleComplete: (task: Task, isCompleted: boolean) => void;
  onRemove: (task: Task) => void;
}) {
  const assignee = lookups.members.find((member) => member.user_id === subtask.assignee_id);

  return (
    <li className={subtask.is_completed ? 'subtask-row subtask-row--complete' : 'subtask-row'}>
      <input
        type="checkbox"
        className="task-check"
        checked={subtask.is_completed}
        disabled={!canEdit}
        aria-label={subtask.is_completed ? `Reopen ${subtask.title}` : `Complete ${subtask.title}`}
        onChange={() => onToggleComplete(subtask, !subtask.is_completed)}
      />
      {canEdit ? (
        <InlineText
          value={subtask.title}
          buttonLabel={`Edit title of ${subtask.title}`}
          inputLabel={`Title of ${subtask.title}`}
          display={<span className="task-title">{subtask.title}</span>}
          allowEmpty={false}
          onSave={(next) => onEdit(subtask, { title: next })}
        />
      ) : (
        <span className="task-title">{subtask.title}</span>
      )}
      {canEdit ? (
        <select
          className="task-cell-input"
          aria-label={`Assignee for ${subtask.title}`}
          value={subtask.assignee_id ?? ''}
          onChange={(event) =>
            onEdit(subtask, {
              assignee_id: event.target.value === '' ? null : event.target.value,
            })
          }
        >
          <option value="">Unassigned</option>
          {lookups.members.map((member) => (
            <option key={member.user_id} value={member.user_id}>
              {memberLabel(member)}
            </option>
          ))}
        </select>
      ) : (
        <span className="subtask-fact">{assignee ? memberLabel(assignee) : 'Unassigned'}</span>
      )}
      {canEdit ? (
        <input
          type="date"
          className="task-cell-input task-cell-input--date"
          aria-label={`Due date for ${subtask.title}`}
          value={subtask.due_date ?? ''}
          onChange={(event) =>
            onEdit(subtask, {
              due_date: event.target.value === '' ? null : event.target.value,
            })
          }
        />
      ) : (
        <span className="subtask-fact">{formatDate(subtask.due_date)}</span>
      )}
      {canEdit ? (
        <Button variant="ghost" size="sm" onClick={() => onRemove(subtask)}>
          Remove
        </Button>
      ) : null}
    </li>
  );
}

export function TaskDetailsPanel({
  task,
  subtasks,
  lookups,
  projectId,
  projectName,
  canEdit,
  canManage,
  compact = false,
  fieldsEditable = false,
  projectVisibility,
  highlightCommentId = null,
  ensureAssignable,
  onClose,
}: {
  task: Task;
  subtasks: Task[];
  lookups: TaskLookups;
  projectId: string;
  /** Names the Drive subfolder uploads land in (Stage 4 attachments). */
  projectName: string;
  /** May edit this project's tasks; the database remains the authority. */
  canEdit: boolean;
  /** May moderate: remove anyone's comment (TAP Admin or project owner). */
  canManage: boolean;
  /**
   * Personal Tasks runs the drawer lightweight (spec §Personal Tasks): a
   * private one-person list has nobody to follow it and no supplier attached,
   * so those sections are hidden rather than shown empty.
   */
  compact?: boolean;
  /**
   * Assignee, status, priority and due date become controls in the panel
   * rather than read-only text.
   *
   * Off by default because on the List View those four are edited in the
   * ROW, and this panel showing them read-only there is a Stage 10 decision
   * that still stands. My Day turns it on: its rows carry no editors, so
   * without this a My Day task could not be given a due date at all, and the
   * hint saying "edited from the task row" pointed at nothing (Hanneza,
   * 7 September). docs/design-note-my-day-field-editing.md.
   */
  fieldsEditable?: boolean;
  /**
   * The project's visibility, used only to say who will be able to open a
   * copied task link. Absent means there is nothing to share — a personal
   * list — and the Copy link control is not rendered at all.
   */
  projectVisibility?: ProjectVisibility;
  /** Comment to scroll to, when the panel was opened by an Inbox deep link. */
  highlightCommentId?: string | null;
  /**
   * Adds someone to the project before assigning them, when an admin picks a
   * person who is not a member yet. The database refuses an assignee who is
   * not on the project, so without this the dropdown offers people it then
   * rejects — which is exactly the bug this panel had for subtasks.
   */
  ensureAssignable: (userId: string | null, then: () => void) => void;
  /** Must be referentially stable: the Dialog focus trap depends on it. */
  onClose: () => void;
}) {
  const [error, setError] = useState('');
  const [description, setDescription] = useState(task.description ?? '');
  const [editingDescription, setEditingDescription] = useState(
    !isRichTextEmpty(task.description ?? ''),
  );
  const descriptionAutoSave = useDebouncedAutoSave<string>((next) => {
    const trimmed = next.trim();
    return new Promise((resolve, reject) => {
      updateTask.mutate(
        { taskId: task.id, changes: { description: trimmed === '' ? null : trimmed } },
        { onSuccess: resolve, onError: reject },
      );
    });
  });
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [pushingToSocial, setPushingToSocial] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [subtaskAssignee, setSubtaskAssignee] = useState('');
  // undefined = untouched, take the workspace default; '' = deliberately
  // cleared. Same three-state shape as the quick-add row, for the same reason:
  // the settings query resolves after the first render.
  const [subtaskDueDate, setSubtaskDueDate] = useState<string | undefined>(undefined);
  const [subtaskDueProblem, setSubtaskDueProblem] = useState('');
  const [subtaskTouched, setSubtaskTouched] = useState(false);
  const [followerId, setFollowerId] = useState('');
  // The add forms sit behind a "+ Add" until asked for (Drop 35): an empty
  // section is one line, not a four-field form for nothing.
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [addingFollower, setAddingFollower] = useState(false);

  const updateTask = useUpdateTask(projectId);
  const setCompleted = useSetTaskCompleted(projectId);
  const createSubtask = useCreateSubtask(projectId);
  const workspace = useTaskDefaults();
  const effectiveSubtaskDue = subtaskDueDate ?? workspace.defaultDueDate ?? '';
  const deleteTask = useDeleteTask(projectId);
  const collaborators = useTaskCollaborators(task.id);
  const addCollaborator = useAddTaskCollaborator(task.id);
  const removeCollaborator = useRemoveTaskCollaborator(task.id);
  const activity = useTaskActivity(task.id);
  // Read here as well as inside the sections, so the folds can say what they
  // hold and open themselves when there is something to see. react-query
  // shares the cache, so this costs no second request.
  const taskFiles = useTaskFiles(task.id, projectId);
  const attachmentCount = groupIntoAssets(taskFiles.data ?? []).length;
  const reviewForTask = useReviewForTask(compact ? undefined : task.id);
  const mutedTaskIds = useMutedTaskIds();
  const muteTask = useMuteTask();
  const unmuteTask = useUnmuteTask();

  // Muting silences future notifications about this task for the signed-in
  // person only (handoff 23). It is not a permission and hides nothing, so it
  // is offered to everyone who can see the task, viewers included.
  const isMuted = (mutedTaskIds.data ?? []).includes(task.id);

  function toggleMute() {
    setError('');
    if (isMuted) {
      unmuteTask.mutate(task.id, { onError: report });
    } else {
      muteTask.mutate(task.id, { onError: report });
    }
  }

  const assignee = lookups.members.find((member) => member.user_id === task.assignee_id);
  const status = lookups.statuses.find((row) => row.id === task.status_id);

  const assigneeOptions: SelectOption[] = [
    { value: '', label: 'Unassigned' },
    ...lookups.members.map((member) => ({ value: member.user_id, label: memberLabel(member) })),
  ];

  // Editable here only when the page asked for it AND this person may edit;
  // a viewer sees the read-only facts whatever the page wants.
  const editsHere = fieldsEditable && canEdit;
  const fieldIds = useId();
  // Active statuses, plus the one this task already holds if it has since
  // been retired — otherwise the select would show a blank for a real value.
  const statusOptions = lookups.statuses.filter(
    (row) => row.is_active || row.id === task.status_id,
  );

  function report(cause: unknown) {
    setError(taskErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function editTask(target: Task, changes: TaskUpdate) {
    setError('');
    // Only an assignee change can trip the membership guard; everything else
    // goes straight through rather than paying for a membership check.
    if (!('assignee_id' in changes)) {
      updateTask.mutate({ taskId: target.id, changes }, { onError: report });
      return;
    }
    ensureAssignable(changes.assignee_id ?? null, () => {
      updateTask.mutate({ taskId: target.id, changes }, { onError: report });
    });
  }

  /** Completion only: never touches status, by locked decision. */
  function toggleComplete(target: Task, isCompleted: boolean) {
    setError('');
    setCompleted.mutate({ taskId: target.id, isCompleted }, { onError: report });
  }

  function addSubtask() {
    const title = subtaskTitle.trim();
    if (title === '') {
      setSubtaskTouched(true);
      return;
    }
    const dueDate = effectiveSubtaskDue === '' ? null : effectiveSubtaskDue;
    const problem = dueDateProblem(dueDate, workspace.requireDueDate);
    if (problem !== null) {
      setSubtaskDueProblem(problem);
      return;
    }
    setSubtaskTouched(false);
    setSubtaskDueProblem('');
    setError('');
    const assigneeId = subtaskAssignee === '' ? null : subtaskAssignee;
    ensureAssignable(assigneeId, () => {
      createSubtask.mutate(
        {
          projectId,
          parentTaskId: task.id,
          title,
          assigneeId,
          dueDate,
        },
        {
          onSuccess: () => {
            setSubtaskTitle('');
            setSubtaskAssignee('');
            setSubtaskDueDate(undefined);
          },
          onError: report,
        },
      );
    });
  }

  function removeSubtask(target: Task) {
    setError('');
    deleteTask.mutate(target.id, { onError: report });
  }

  function addFollower() {
    if (followerId === '') {
      setError('Choose a person to add as a follower.');
      return;
    }
    setError('');
    addCollaborator.mutate(followerId, {
      onSuccess: () => setFollowerId(''),
      onError: report,
    });
  }

  function removeTask() {
    setError('');
    deleteTask.mutate(task.id, { onSuccess: onClose, onError: report });
  }

  return (
    <Drawer
      open
      title={task.title}
      onClose={onClose}
      menuItems={
        canEdit
          ? [
              // First, because it is the reason this menu grew. The drawer
              // heading is the task's name and was the one place in the app
              // that showed a name it could not change — Kyu, 14 September:
              // "We need to be able to rename tasks we've already created."
              { label: 'Rename', onSelect: () => setRenaming(true) },
              // Already-pushed tasks keep the item but the database refuses a
              // second push, and the dialog says so — hiding it would leave
              // somebody hunting for an action that used to be there.
              {
                label: '🚀 Push to Social Studio…',
                onSelect: () => setPushingToSocial(true),
              },
              { label: 'Delete task…', onSelect: () => setConfirmingDelete(true), danger: true },
            ]
          : undefined
      }
    >
      {/* Above everything, because the heading it replaces is above
          everything. The drawer's own <h2> stays put and keeps naming the
          drawer for screen readers; this is the editable copy, and it goes
          away the moment it is committed or cancelled. */}
      {renaming ? (
        <div className="task-rename-field">
          <InlineTextInput
            value={task.title}
            inputLabel={`Title of ${task.title}`}
            allowEmpty={false}
            onSave={(next) => {
              updateTask.mutate({ taskId: task.id, changes: { title: next } }, { onError: report });
            }}
            onClose={() => setRenaming(false)}
          />
        </div>
      ) : null}

      {pushingToSocial ? (
        <PushToSocialDialog
          task={task}
          projectId={projectId}
          onClose={() => setPushingToSocial(false)}
          onPushed={() => setPushingToSocial(false)}
        />
      ) : null}

      <Dialog
        open={confirmingDelete}
        title="Delete task?"
        onClose={() => setConfirmingDelete(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
              Keep task
            </Button>
            <Button variant="danger" onClick={removeTask} disabled={deleteTask.isPending}>
              {deleteTask.isPending ? 'Deleting…' : 'Confirm delete'}
            </Button>
          </>
        }
      >
        <p>This deletes the task and its subtasks. The activity history is kept.</p>
      </Dialog>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {/* A task's link is the project's link carrying the task — the same
          shape the Inbox has always built. It sits at the top of the panel
          because that is where people look when they are about to send
          somebody here, and the note under it names who can actually open
          it: the link grants nothing, so the warning has to arrive before
          the link is sent, not after. */}
      {projectVisibility === undefined ? null : (
        <CopyLinkButton
          url={taskShareUrl(window.location.origin, projectId, task.id)}
          visibility={projectVisibility}
          label="Copy task link"
          className="task-panel-share"
        />
      )}

      <div className="task-panel-complete">
        {canEdit ? (
          <Checkbox
            label={task.is_completed ? 'Completed — untick to reopen' : 'Mark complete'}
            checked={task.is_completed}
            onChange={() => toggleComplete(task, !task.is_completed)}
          />
        ) : task.is_completed ? (
          <Badge tone="success">Completed</Badge>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleMute}
          disabled={muteTask.isPending || unmuteTask.isPending}
        >
          {isMuted ? 'Unmute notifications' : 'Mute notifications'}
        </Button>
      </div>

      {/* The same 92px label track the record profiles use. A task's facts are
          properties, not one run-on sentence, and the hairlines let the eye
          keep its place without turning this into another table. */}
      <dl className="task-facts">
        {editsHere ? (
          /* The same native controls the List View row uses, with the same
             accessible names, writing through the same editTask — so a My Day
             task and a project task are edited by one code path, and a future
             change to either shows up in both. */
          <>
            <div>
              <dt>
                <label htmlFor={`${fieldIds}-assignee`}>Assignee</label>
              </dt>
              <dd>
                <select
                  id={`${fieldIds}-assignee`}
                  className="task-cell-input"
                  aria-label={`Assignee for ${task.title}`}
                  value={task.assignee_id ?? ''}
                  onChange={(event) =>
                    editTask(task, {
                      assignee_id: event.target.value === '' ? null : event.target.value,
                    })
                  }
                >
                  {assigneeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div>
              <dt>
                <label htmlFor={`${fieldIds}-status`}>Status</label>
              </dt>
              <dd>
                <select
                  id={`${fieldIds}-status`}
                  className="task-cell-input"
                  aria-label={`Status for ${task.title}`}
                  value={task.status_id ?? ''}
                  onChange={(event) =>
                    editTask(task, {
                      status_id: event.target.value === '' ? null : event.target.value,
                    })
                  }
                >
                  <option value="">No status</option>
                  {statusOptions.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div>
              <dt>
                <label htmlFor={`${fieldIds}-priority`}>Priority</label>
              </dt>
              <dd>
                <select
                  id={`${fieldIds}-priority`}
                  className="task-cell-input"
                  aria-label={`Priority for ${task.title}`}
                  value={task.priority ?? ''}
                  onChange={(event) =>
                    editTask(task, {
                      priority:
                        event.target.value === '' ? null : (event.target.value as TaskPriority),
                    })
                  }
                >
                  <option value="">None</option>
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div>
              <dt>
                <label htmlFor={`${fieldIds}-due`}>Due</label>
              </dt>
              <dd>
                {/* Same wrapper the row uses: an unset date reads as an em
                    dash rather than the OS's mm/dd/yyyy placeholder. */}
                <span className={`task-date${task.due_date === null ? ' task-date--empty' : ''}`}>
                  <input
                    id={`${fieldIds}-due`}
                    type="date"
                    className="task-cell-input task-cell-input--date"
                    aria-label={`Due date for ${task.title}`}
                    value={task.due_date ?? ''}
                    onChange={(event) =>
                      editTask(task, {
                        due_date: event.target.value === '' ? null : event.target.value,
                      })
                    }
                  />
                </span>
              </dd>
            </div>
          </>
        ) : (
          <>
            <div>
              <dt>Assignee</dt>
              <dd>{assignee ? memberLabel(assignee) : 'Unassigned'}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                {status ? (
                  <span
                    className={`hub-status status-pill hub-status--${statusTone(status.color_key)}`}
                  >
                    {status.name}
                  </span>
                ) : (
                  <span className="task-fact-empty">——</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Priority</dt>
              <dd>
                {task.priority ? (
                  TASK_PRIORITY_LABELS[task.priority]
                ) : (
                  <span className="task-fact-empty">——</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Due</dt>
              <dd>
                {task.due_date === null ? (
                  <span className="task-fact-empty">——</span>
                ) : (
                  formatDate(task.due_date)
                )}
              </dd>
            </div>
          </>
        )}
        {task.assigned_at === null ? null : (
          <div>
            <dt>Assigned</dt>
            <dd>{formatDate(task.assigned_at)}</dd>
          </div>
        )}
        {task.completed_at === null ? null : (
          <div>
            <dt>Completed</dt>
            <dd>{formatDate(task.completed_at)}</dd>
          </div>
        )}
      </dl>
      {canEdit && !editsHere ? (
        <p className="task-panel-hint">
          Assignee, status, priority, due date and remarks are edited from the task row in the list.
        </p>
      ) : null}

      {/* The project's own fields, edited here rather than in the row.
          They are per-project and there may be four of them with eight options
          each — a task row that carried all that would be unreadable on every
          board that has none. Renders nothing at all on those. */}
      <FieldPicker projectId={projectId} taskId={task.id} canEdit={canEdit} />

      <div className="task-panel-section task-panel-description">
        <RowGroup title="Description">
          <div className="task-panel-card-body">
            {canEdit ? (
              !editingDescription && isRichTextEmpty(description) ? (
                <div className="task-panel-empty">
                  <p>No description yet</p>
                  <Button variant="ghost" size="sm" onClick={() => setEditingDescription(true)}>
                    Add description
                  </Button>
                </div>
              ) : (
                <>
                  <RichTextEditor
                    label="Description"
                    value={description}
                    placeholder="Add a description…"
                    onChange={(next) => {
                      setDescription(next);
                      descriptionAutoSave.save(next);
                    }}
                  />
                  <SaveHint state={descriptionAutoSave.state} error={descriptionAutoSave.error} />
                </>
              )
            ) : isRichTextEmpty(task.description ?? '') ? (
              <p className="task-panel-text">No description yet</p>
            ) : (
              <RichTextView value={task.description ?? ''} />
            )}
          </div>
        </RowGroup>
      </div>

      <div className="task-panel-section task-panel-comments">
        <RowGroup title="Comments">
          <div className="task-panel-card-body">
            <CommentsSection
              taskId={task.id}
              projectId={projectId}
              canComment={canEdit}
              canManage={canManage}
              members={lookups.members}
              highlightCommentId={highlightCommentId}
              heading=""
            />
          </div>
        </RowGroup>
      </div>

      {/* The one line in the panel: between what people came for and what
          they occasionally set. */}
      <div className="task-panel-group-rule" aria-hidden="true" />

      {/* The four folds as one card of rows — caret, name, what is inside,
          and a hairline between them — so what people occasionally set reads
          as a grouped list rather than four loose headings. Same object as a
          Settings row; the caret is its chevron. */}
      <div className="panel-folds">
        <PanelFold
          title="Subtasks"
          summary={subtasks.length === 0 ? 'none' : String(subtasks.length)}
          defaultOpen={subtasks.length > 0}
          action={
            canEdit ? (
              <Button
                variant="ghost"
                size="sm"
                aria-expanded={addingSubtask}
                onClick={() => setAddingSubtask((open) => !open)}
              >
                + Add subtask
              </Button>
            ) : undefined
          }
        >
          {subtasks.length === 0 ? (
            <p className="task-panel-text">No subtasks yet</p>
          ) : (
            <ul className="subtask-list">
              {subtasks.map((subtask) => (
                <SubtaskRow
                  key={subtask.id}
                  subtask={subtask}
                  lookups={lookups}
                  canEdit={canEdit}
                  onEdit={editTask}
                  onToggleComplete={toggleComplete}
                  onRemove={removeSubtask}
                />
              ))}
            </ul>
          )}
        </PanelFold>
        {canEdit && addingSubtask ? (
          <div className="subtask-add">
            <Input
              label="Subtask name"
              value={subtaskTitle}
              onChange={(event) => setSubtaskTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addSubtask();
                }
              }}
              {...(subtaskTouched ? { error: 'Enter a subtask name.' } : {})}
            />
            <Select
              label="Assignee"
              options={assigneeOptions}
              value={subtaskAssignee}
              onChange={(event) => setSubtaskAssignee(event.target.value)}
            />
            <Input
              label="Due date"
              type="date"
              value={effectiveSubtaskDue}
              {...(subtaskDueProblem === '' ? {} : { error: subtaskDueProblem })}
              onChange={(event) => {
                setSubtaskDueDate(event.target.value);
                setSubtaskDueProblem('');
              }}
            />
            <Button onClick={addSubtask} disabled={createSubtask.isPending}>
              {createSubtask.isPending ? 'Adding…' : 'Add subtask'}
            </Button>
          </div>
        ) : null}

        <PanelFold
          title="Attachments"
          summary={
            taskFiles.isPending
              ? undefined
              : attachmentCount === 0
                ? 'none'
                : String(attachmentCount)
          }
          defaultOpen={attachmentCount > 0}
        >
          <FilesSection
            taskId={task.id}
            projectId={projectId}
            projectName={projectName}
            canEdit={canEdit}
            canManage={canManage}
            heading=""
          />
        </PanelFold>

        {compact ? null : (
          <PanelFold
            title="Followers"
            summary={
              collaborators.isPending
                ? undefined
                : (collaborators.data ?? []).length === 0
                  ? 'none'
                  : String((collaborators.data ?? []).length)
            }
            defaultOpen={(collaborators.data ?? []).length > 0}
            action={
              canEdit ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-expanded={addingFollower}
                  onClick={() => setAddingFollower((open) => !open)}
                >
                  + Add follower
                </Button>
              ) : undefined
            }
          >
            {collaborators.isPending ? (
              <Skeleton lines={1} label="Loading followers" />
            ) : collaborators.isError ? (
              <p className="task-panel-text">Followers could not be loaded.</p>
            ) : (collaborators.data ?? []).length === 0 ? (
              <p className="task-panel-text">No followers yet</p>
            ) : (
              <ul className="follower-list">
                {(collaborators.data ?? []).map((follower) => {
                  const name =
                    follower.display_name.trim() !== '' ? follower.display_name : follower.email;
                  return (
                    <li key={follower.id} className="follower-row">
                      <span>{name}</span>
                      {canEdit ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove follower ${name}`}
                          onClick={() => {
                            setError('');
                            removeCollaborator.mutate(follower.id, { onError: report });
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </PanelFold>
        )}
        {!compact && canEdit && addingFollower ? (
          <div className="follower-add">
            <Select
              label="Add follower"
              value={followerId}
              onChange={(event) => setFollowerId(event.target.value)}
              options={[
                { value: '', label: 'Choose a person' },
                ...lookups.members
                  .filter(
                    (member) =>
                      !(collaborators.data ?? []).some(
                        (follower) => follower.user_id === member.user_id,
                      ),
                  )
                  .map((member) => ({ value: member.user_id, label: memberLabel(member) })),
              ]}
            />
            <Button onClick={addFollower} disabled={addCollaborator.isPending}>
              {addCollaborator.isPending ? 'Adding…' : 'Add follower'}
            </Button>
          </div>
        ) : null}

        {compact ? null : (
          <PanelFold
            title="Supplier review"
            summary={
              reviewForTask.isPending
                ? undefined
                : reviewForTask.data === null
                  ? 'not sent'
                  : 'sent'
            }
            defaultOpen={reviewForTask.data !== null && reviewForTask.data !== undefined}
          >
            <SupplierReviewSection
              task={task}
              projectId={projectId}
              canEdit={canEdit}
              canManage={canManage}
              heading=""
            />
          </PanelFold>
        )}
      </div>

      <h3 className="task-panel-heading">Activity</h3>
      {activity.isPending ? (
        <Skeleton lines={2} label="Loading activity" />
      ) : activity.isError ? (
        <p className="task-panel-text">Activity could not be loaded.</p>
      ) : (activity.data ?? []).length === 0 ? (
        <p className="task-panel-text">No activity yet</p>
      ) : (
        <ul className="activity-list">
          {(activity.data ?? []).map((entry) => (
            <li key={entry.id} className="activity-row">
              <span className="activity-text">
                <strong>{entry.actor_name === '' ? 'Someone' : entry.actor_name}</strong>{' '}
                {TASK_ACTIVITY_LABELS[entry.event_type]}
              </span>
              <span className="activity-date">{formatDate(entry.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
